import { store } from "../store";
import { handleUnauthorizedError } from "./apiMiddleware";

export interface Retailer {
  id?: string;
  _id?: string;
  name: string;
  group?: string;
  channelsDetails?: Array<{
    key?: string;
    name?: string;
    type?: "pos" | "ecom" | "marketplace" | "department_store" | "wholesale";
    status?: "planned" | "active" | "paused";
    launchedAt?: string;
    stoppedAt?: string;
    coverage?: {
      countries?: string[];
      regions?: string[];
      doors?: number;
      notes?: string;
    };
    location?: {
      label?: string;
      street?: string;
      city?: string;
      postalCode?: string;
      country?: string;
      region?: string;
      latitude?: number;
      longitude?: number;
    };
    logistics?: {
      fulfillmentMode?: "hub" | "direct_store";
      hub?: {
        label?: string;
        street?: string;
        city?: string;
        postalCode?: string;
        country?: string;
        region?: string;
        latitude?: number;
        longitude?: number;
      };
      notes?: string;
    };
    notes?: string;
  }>;
  segment?: "A" | "B" | "C";
  status?: "active" | "paused" | "prospect" | "dormant";
  crm?: {
    key?: string;
    columnKey?: string;
    onboardingStage: "lead" | "discovery" | "qualified" | "negotiation" | "contract" | "onboarding" | "launched" | "lost";
    onboardingPriority?: "low" | "medium" | "high";
    proposedBrand?: string;
    probability?: number;
    estimatedValue?: number;
    leadSource?: string;
    lastContactDate?: string;
    nextActionDate?: string;
    nextActionLabel?: string;
    assignedTo?: string;
    validation?: {
      approvedAt?: string;
      approvedBy?: string;
      approvedByName?: string;
    };
    stageHistory?: {
      lead?: { reached: boolean; reachedAt?: string };
      discovery?: { reached: boolean; reachedAt?: string };
      qualified?: { reached: boolean; reachedAt?: string };
      negotiation?: { reached: boolean; reachedAt?: string };
      contract?: { reached: boolean; reachedAt?: string };
      onboarding?: { reached: boolean; reachedAt?: string };
      launched?: { reached: boolean; reachedAt?: string };
      lost?: { reached: boolean; reachedAt?: string };
    };
    checklists?: {
      lead?: Array<{ task: string; completed: boolean }>;
      discovery?: Array<{ task: string; completed: boolean }>;
      qualified?: Array<{ task: string; completed: boolean }>;
      negotiation?: Array<{ task: string; completed: boolean }>;
      contract?: Array<{ task: string; completed: boolean }>;
      onboarding?: Array<{ task: string; completed: boolean }>;
      launched?: Array<{ task: string; completed: boolean }>;
      lost?: Array<{ task: string; completed: boolean }>;
    };
  };
  createdBy?: {
    id?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  };
  kyc?: {
    registrationNumber?: string;
    annualRevenue?: number;
    posCount?: number;
    hasEshop?: boolean;
    eshopUrl?: string;
    marketScopeType?: "cities" | "regions" | "countries";
    marketCities?: string[];
    marketRegions?: string[];
    marketCountries?: string[];
    notes?: string;
  };
  contracts?: {
    discountRate?: number;
    incoterms?: string;
    moq?: number;
    exclusivity?: string;
    territories?: string[];
    paymentTerms?: string;
    penalties?: string;
  };
  logistics?: {
    slaDays?: number;
    ediProvider?: string;
    leadTimeDays?: number;
    warehouseNotes?: string;
    fulfillmentMode?: "hub" | "multi_hub" | "per_channel";
    hub?: {
      label?: string;
      street?: string;
      city?: string;
      postalCode?: string;
      country?: string;
      region?: string;
      latitude?: number;
      longitude?: number;
    };
    hubs?: Array<{
      label?: string;
      address?: {
        label?: string;
        street?: string;
        city?: string;
        postalCode?: string;
        country?: string;
        region?: string;
        latitude?: number;
        longitude?: number;
      };
      channelKeys?: string[];
    }>;
  };
  contacts?: Array<{
    name?: string;
    role?: string;
    department?: string;
    email?: string;
    phone?: string;
    isDecisionMaker?: boolean;
    contactType?: "general" | "accounting";
  }>;
  governance?: {
    orgNotes?: string;
  };
  risk?: {
    score?: number;
    paymentDelays?: number;
    disputes?: number;
    sellOutVariance?: number;
    stockouts?: number;
    returnRate?: number;
  };
  performance?: {
    sellIn?: {
      revenue?: number;
      volume?: number;
      grossMargin?: number;
      contributionMargin?: number;
    };
    sellOut?: {
      revenue?: number;
      units?: number;
      rotationSpeed?: number;
      bestSkus?: string[];
      worstSkus?: string[];
    };
    assortment?: {
      listingRate?: number;
      depth?: number;
      doors?: number;
    };
    quality?: {
      stockoutRate?: number;
      returnRate?: number;
      disputes?: number;
      otif?: number;
      docsCompliance?: number;
    };
  };
  finance?: {
    creditLimit?: number;
    outstandingBalance?: number;
    paymentDelayDays?: number;
    riskLevel?: string;
    paymentTermsPolicy?: {
      baseDate?: "reception" | "order" | "invoice";
      endOfMonth?: boolean;
      extraDays?: number;
    };
  };
  notes?: string;
  projects?: Project[];
  projectsPriorityFlag?: 'green' | 'yellow' | 'red';
  accountManager?: {
    id?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
  };
}

export interface Project {
  _id?: string;
  retailerId?: string;
  supplierId?: string;
  storageId?: string;
  externalEntityName?: string;
  collaborators?: string[];
  creator?: string;
  departmentId?: string;
  title: string;
  description?: string;
  status: 'open' | 'completed' | 'on_hold' | 'cancelled' | 'archived';
  priority: 'low' | 'medium' | 'high';
  startDate?: string;
  endDate?: string;
  lastEmailSubject?: string;
  lastEmailPreview?: string;
  lastEmailAt?: string;
  lastEmailFrom?: string;
  unreadEmailCount?: number;
  pushPullStatus?: 'push' | 'pull';
  statusLabel?: string;
  priorityFlag?: 'green' | 'yellow' | 'red';
  attachedEmailIds: string[];
  attachedConversationIds?: string[];
  interlocutors?: Array<{
    email?: string;
    name?: string;
    firstName?: string;
    lastName?: string;
  }>;
  checklists?: {
    todo?: Array<{ task: string; completed: boolean }>;
    in_progress?: Array<{ task: string; completed: boolean }>;
  };
  taskBoard?: {
    todo?: Array<{
      title: string;
      status?: "active" | "archived";
      source?: "ai" | "manual" | "default";
      createdAt?: string;
      archivedAt?: string;
    }>;
    in_progress?: Array<{
      title: string;
      status?: "active" | "archived";
      source?: "ai" | "manual" | "default";
      createdAt?: string;
      archivedAt?: string;
    }>;
  };
  customFields?: Array<{
    key: string;
    label: string;
    type:
      | 'text'
      | 'long_text'
      | 'number'
      | 'boolean'
      | 'date'
      | 'datetime'
      | 'date_range'
      | 'select'
      | 'multi_select'
      | 'user'
      | 'url'
      | 'email'
      | 'phone'
      | 'currency'
      | 'percentage';
    value?: any;
    options?: string[];
  }>;
  attachments?: Array<{
    key?: string;
    itemId?: string;
    name?: string;
    contentType?: string;
    size?: number;
    source?: string;
  }>;
  customFieldsDisplay?: string[];
  metadata?: Record<string, string>;
  createdAt?: string;
  updatedAt?: string;
}

export interface RetailerMailMessage {
  id?: string;
  subject?: string;
  from?: string;
  receivedDateTime?: string;
  preview?: string;
  summary?: string;
  aiSummary?: string;
  threadSummary?: string;
  isImportant?: boolean;
  isRead?: boolean;
  conversationId?: string;
  hasAttachments?: boolean;
  mailboxIdentity?: string;
  attachments?: Array<{
    id: string;
    name?: string;
    contentType?: string;
    size?: number;
    isInline?: boolean;
    downloadUrl?: string;
  }>;
}

export interface RetailerMailAlert {
  count: number;
  messages: RetailerMailMessage[];
}

const API_URL = `${import.meta.env.VITE_API_URL || "http://localhost:5000/api"}/retailers`;

const getAuthHeader = (): Record<string, string> => {
  const token = store.getState().user?.currentUser?.token;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const getRetailerMailAlerts = async (): Promise<Record<string, RetailerMailAlert>> => {
  const response = await fetch(`${API_URL}/mail-alerts`, {
    headers: {
      ...getAuthHeader(),
    },
  });

  if (handleUnauthorizedError(response)) {
    throw new Error("Session expirée ou non autorisée");
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Erreur HTTP: ${response.status}`);
  }

  return response.json();
};

export const summarizeRetailerEmail = async (
  retailerId: string,
  messageId: string,
  payload?: { subject?: string; preview?: string }
): Promise<{ summary: string; cached: boolean }> => {
  const response = await fetch(`${API_URL}/${retailerId}/emails/${messageId}/summary`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
    },
    body: JSON.stringify(payload || {}),
  });
  if (!response.ok) {
    throw new Error('Erreur lors du résumé IA');
  }
  return response.json();
};

export const summarizeRetailerThread = async (
  retailerId: string,
  threadId: string
): Promise<{ summary: string; cached: boolean }> => {
  const response = await fetch(`${API_URL}/${retailerId}/threads/${threadId}/summary`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
    },
  });
  if (!response.ok) {
    throw new Error('Erreur lors du résumé IA (thread)');
  }
  return response.json();
};

export const updateRetailerEmailImportance = async (
  retailerId: string,
  messageId: string,
  important: boolean
): Promise<{ important: boolean }> => {
  const response = await fetch(`${API_URL}/${retailerId}/emails/${messageId}/important`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader(),
    },
    body: JSON.stringify({ important }),
  });
  if (!response.ok) {
    throw new Error('Erreur lors du flag important');
  }
  return response.json();
};

const normalizeRetailer = (item: any): Retailer => ({
  ...item,
  id: item._id || item.id,
});

export const getRetailerEmails = async (id: string): Promise<RetailerMailMessage[]> => {
  const response = await fetch(`${API_URL}/${id}/emails`, {
    headers: {
      ...getAuthHeader(),
    },
  });
  if (handleUnauthorizedError(response)) {
    throw new Error("Session expirée");
  }
  return response.json();
};

export const createProject = async (id: string, project: Partial<Project>): Promise<Project> => {
  const response = await fetch(`${API_URL}/${id}/projects`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeader(),
    },
    body: JSON.stringify(project),
  });
  if (handleUnauthorizedError(response)) {
    throw new Error("Session expirée");
  }
  return response.json();
};

export const updateProject = async (retailerId: string, projectId: string, project: Partial<Project>): Promise<Project> => {
  const response = await fetch(`${API_URL}/${retailerId}/projects/${projectId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeader(),
    },
    body: JSON.stringify(project),
  });
  if (handleUnauthorizedError(response)) {
    throw new Error("Session expirée");
  }
  return response.json();
};

export const deleteProject = async (retailerId: string, projectId: string): Promise<void> => {
  const response = await fetch(`${API_URL}/${retailerId}/projects/${projectId}`, {
    method: "DELETE",
    headers: {
      ...getAuthHeader(),
    },
  });
  if (handleUnauthorizedError(response)) {
    throw new Error("Session expirée");
  }
};

export const getRetailers = async (params?: {
  search?: string;
  country?: string;
  segment?: string;
  status?: string;
}): Promise<Retailer[]> => {
  const query = params
    ? `?${new URLSearchParams(
        Object.entries(params).filter(([, v]) => v) as Array<[string, string]>
      ).toString()}`
    : "";

  const response = await fetch(`${API_URL}${query}`, {
    headers: {
      ...getAuthHeader(),
    },
  });

  if (handleUnauthorizedError(response)) {
    throw new Error("Session expirée ou non autorisée");
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Erreur HTTP: ${response.status}`);
  }

  const data = await response.json();
  return Array.isArray(data) ? data.map(normalizeRetailer) : [];
};

export const createRetailer = async (payload: Retailer): Promise<Retailer> => {
  const response = await fetch(API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeader(),
    },
    body: JSON.stringify(payload),
  });

  if (handleUnauthorizedError(response)) {
    throw new Error("Session expirée ou non autorisée");
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Erreur HTTP: ${response.status}`);
  }

  const data = await response.json();
  return normalizeRetailer(data);
};

export const updateRetailer = async (
  id: string,
  payload: Partial<Retailer>
): Promise<Retailer> => {
  const response = await fetch(`${API_URL}/${id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeader(),
    },
    body: JSON.stringify(payload),
  });

  if (handleUnauthorizedError(response)) {
    throw new Error("Session expirée ou non autorisée");
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Erreur HTTP: ${response.status}`);
  }

  const data = await response.json();
  return normalizeRetailer(data);
};

export const reassignRetailerCrm = async (payload: {
  fromCrmKey: string;
  toCrmKey?: string;
  fromColumnKeys?: string[];
  toColumnKey: string;
}): Promise<{ matched: number; modified: number }> => {
  const response = await fetch(`${API_URL}/crm/reassign`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeader(),
    },
    body: JSON.stringify(payload),
  });

  if (handleUnauthorizedError(response)) {
    throw new Error("Session expirée ou non autorisée");
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Erreur HTTP: ${response.status}`);
  }

  return response.json();
};

export const deleteRetailer = async (id: string): Promise<void> => {
  const response = await fetch(`${API_URL}/${id}`, {
    method: "DELETE",
    headers: {
      ...getAuthHeader(),
    },
  });

  if (handleUnauthorizedError(response)) {
    throw new Error("Session expirée ou non autorisée");
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Erreur HTTP: ${response.status}`);
  }
};

export const assignAccountManager = async (id: string, userId: string): Promise<Retailer> => {
  const response = await fetch(`${API_URL}/${id}/account-manager`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeader(),
    },
    body: JSON.stringify({ userId }),
  });

  if (handleUnauthorizedError(response)) {
    throw new Error("Session expirée ou non autorisée");
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Erreur HTTP: ${response.status}`);
  }

  const data = await response.json();
  return normalizeRetailer(data);
};
